/**
 * Modal Component - Unified modal for Sked Social
 *
 * A fully Tailwind-styled modal component with sensible defaults.
 * Supports page scrolling (modal extends to content height, user scrolls page).
 * Supports stacked modals with automatic z-index management.
 *
 * Usage:
 * <Modal show={isOpen} onHide={handleClose}>
 *   <Modal.Header closeButton>
 *     <Modal.Title>Title</Modal.Title>
 *   </Modal.Header>
 *   <Modal.Body>Content</Modal.Body>
 *   <Modal.Footer>Actions</Modal.Footer>
 * </Modal>
 */
import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../lib/utils";
import CloseIcon from "./close-icon.svg";
import {
  useModalStack,
  ModalStackProvider,
  isInsideRadixPortal,
  Z_INDEX_MODAL_BACKDROP,
  Z_INDEX_MODAL_CONTAINER,
  Z_INDEX_MODAL_CONTENT,
  Z_INDEX_STEP,
} from "../../lib/modal-stack";

const FALLBACK_DIALOG_TITLE = "Dialog";

function hasDialogTitle(children: React.ReactNode): boolean {
  let foundTitle = false;

  React.Children.forEach(children, (child) => {
    if (foundTitle || !React.isValidElement(child)) {
      return;
    }

    if (
      child.type === ModalTitle ||
      child.type === DialogPrimitive.Title ||
      child.props["data-slot"] === "dialog-title"
    ) {
      foundTitle = true;
      return;
    }

    if ("children" in child.props) {
      foundTitle = hasDialogTitle(child.props.children);
    }
  });

  return foundTitle;
}

// ============================================================================
// Modal Component
// ============================================================================

export interface ModalProps {
  show?: boolean;
  onHide?: () => void;
  onExited?: () => void;
  onEntered?: () => void;
  /** @deprecated Use onEntered instead */
  onShow?: () => void;
  animation?: boolean;
  backdrop?: boolean | "static";
  keyboard?: boolean;
  /** @deprecated No longer has any effect */
  enforceFocus?: boolean;
  /** @deprecated No longer has any effect */
  autoFocus?: boolean;
  /** @deprecated Use className instead. Kept for backwards compatibility with legacy-modal. */
  dialogClassName?: string;
  className?: string;
  contentClassName?: string;
  /** @deprecated Legacy bootstrap style prop - no longer has any effect */
  bsStyle?: string;
  /** @deprecated Legacy bootstrap size prop - no longer has any effect */
  bsSize?: string;
  /** @deprecated Legacy bootstrap size prop - no longer has any effect */
  size?: "sm" | "lg" | "xl";
  /** @deprecated Use Modal.Header with Modal.Title instead */
  title?: string;
  children?: React.ReactNode;
  "data-testid"?: string;
  /** When true, adds data-scope="content" to backdrop/container for CSS targeting to scope modal within content area */
  scopeToContent?: boolean;
  /**
   * When true (default), creates a modal dialog with focus trapping and blocked outside interactions.
   * Set to false for special cases like page-scrolling modals where the user scrolls the page itself.
   */
  modal?: boolean;
  /**
   * Optional z-index offset applied to backdrop/container/content.
   * Useful when stacking modals rendered from different roots/registries.
   */
  zIndexOffset?: number;
  /** Radix's close auto-focus hook; call `preventDefault` to place focus yourself. */
  onCloseAutoFocus?: (event: Event) => void;
}

function Modal({
  show = false,
  onHide,
  onExited,
  onEntered,
  onShow,
  animation = true,
  backdrop = true,
  keyboard = true,
  enforceFocus: _enforceFocus,
  autoFocus: _autoFocus,
  dialogClassName,
  className,
  contentClassName,
  bsStyle: _bsStyle,
  bsSize: _bsSize,
  size: _size,
  title: _title,
  children,
  "data-testid": dataTestId,
  scopeToContent,
  modal: modalProp = true,
  zIndexOffset = 0,
  onCloseAutoFocus,
}: ModalProps) {
  const depth = useModalStack();
  const needsFallbackTitle = !hasDialogTitle(children);

  // Track when modal is "ready" to accept backdrop clicks
  // This is a safety net for modal={false} cases where Radix doesn't automatically
  // block outside interactions. Prevents clicks from dropdown menus etc. from
  // immediately closing the modal during the transition period.
  const [isReadyForInteraction, setIsReadyForInteraction] =
    React.useState(false);

  const handleOpenChange = (open: boolean) => {
    if (!open && onHide) {
      onHide();
    }
  };

  // Reset ready state when modal opens/closes, and set ready after a short delay.
  // Only relevant for modal={false} cases; modal={true} has Radix handling this.
  React.useEffect(() => {
    if (show) {
      setIsReadyForInteraction(false);
      const timer = setTimeout(() => {
        setIsReadyForInteraction(true);
      }, 100); // 100ms delay before accepting backdrop clicks
      return () => clearTimeout(timer);
    } else {
      setIsReadyForInteraction(false);
    }
    return undefined;
  }, [show]);

  // Handle onExited callback when dialog closes
  React.useEffect(() => {
    if (!show && onExited) {
      const timer = setTimeout(onExited, 150);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [show, onExited]);

  // Handle onEntered callback when dialog opens
  React.useEffect(() => {
    if (show && onEntered) {
      const timer = setTimeout(onEntered, 150);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [show, onEntered]);

  // Handle legacy onShow callback when dialog opens (same as onEntered)
  React.useEffect(() => {
    if (show && onShow) {
      const timer = setTimeout(onShow, 150);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [show, onShow]);

  // Handler for backdrop click - only fires if modal is ready for interaction.
  // Safety net for modal={false} cases.
  const handleBackdropClick = React.useCallback(() => {
    if (isReadyForInteraction && backdrop === true && onHide) {
      onHide();
    }
  }, [isReadyForInteraction, backdrop, onHide]);

  // Handler for pointer down outside - prevents closing if not ready or if event is from a Radix portal.
  // Safety net for modal={false} cases and nested Radix components.
  const handlePointerDownOutside = React.useCallback(
    (e: Event) => {
      if (!isReadyForInteraction || backdrop === "static") {
        e.preventDefault();
        return;
      }
      if (isInsideRadixPortal(e)) {
        e.preventDefault();
      }
    },
    [isReadyForInteraction, backdrop]
  );

  // Handler for focus/interact outside - prevents closing if not ready or if event is from a Radix portal.
  // Essential safety net for modal={false} cases where focus can escape.
  const handleInteractOutside = React.useCallback(
    (e: Event) => {
      if (!isReadyForInteraction || backdrop === "static") {
        e.preventDefault();
        return;
      }
      if (isInsideRadixPortal(e)) {
        e.preventDefault();
      }
    },
    [isReadyForInteraction, backdrop]
  );

  // Calculate z-index values based on modal depth
  const zIndexBackdrop =
    Z_INDEX_MODAL_BACKDROP + depth * Z_INDEX_STEP + zIndexOffset;
  const zIndexContainer =
    Z_INDEX_MODAL_CONTAINER + depth * Z_INDEX_STEP + zIndexOffset;
  const zIndexContent =
    Z_INDEX_MODAL_CONTENT + depth * Z_INDEX_STEP + zIndexOffset;

  // Positioning styles for content-scoped modals. The menu updates this
  // offset when it expands so navigation stays outside the modal layer.
  const contentScopePositioning = scopeToContent
    ? {
        top: 0,
        right: 0,
        bottom: 0,
        insetInlineStart: "var(--menu-modal-offset)",
      }
    : undefined;

  const modalClassName = cn(
    // Positioning - uses margin for centering instead of CSS transform
    // (transforms create a new stacking context that breaks drag-and-drop coordinate calculations)
    "relative mt-8 mb-8 mx-auto",
    // Appearance - Sked design system defaults
    "bg-white rounded-2xl shadow-lg",
    // Size constraints
    "w-fit min-w-[640px] max-w-[90vw]",
    // Layout
    "flex flex-col",
    // Animation
    animation &&
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:fill-mode-forwards duration-200",
    // Custom classes (allows overriding all defaults) - dialogClassName for backwards compatibility
    dialogClassName,
    className
  );

  return (
    <DialogPrimitive.Root
      open={show}
      onOpenChange={handleOpenChange}
      modal={modalProp}
    >
      <DialogPrimitive.Portal>
        {/* Custom backdrop - fixed to viewport, doesn't scroll with content */}
        {backdrop !== false && (
          <div
            data-slot="modal-backdrop"
            className={cn(
              "fixed bg-black/50 backdrop-blur-sm",
              !scopeToContent && "inset-0",
              animation && "transition-opacity duration-200",
              show ? "opacity-100" : "opacity-0 pointer-events-none"
            )}
            style={{
              zIndex: zIndexBackdrop,
              ...contentScopePositioning,
            }}
            onClick={handleBackdropClick}
            aria-hidden="true"
            data-scope={scopeToContent ? "content" : undefined}
          />
        )}

        {/* Scrollable container for modal content */}
        <div
          data-slot="modal-container"
          className={cn("fixed overflow-y-auto", !scopeToContent && "inset-0")}
          style={{
            zIndex: zIndexContainer,
            ...contentScopePositioning,
          }}
          data-scope={scopeToContent ? "content" : undefined}
        >
          <DialogPrimitive.Content
            data-slot="modal-content"
            data-testid={dataTestId}
            className={modalClassName}
            style={{ zIndex: zIndexContent }}
            onEscapeKeyDown={keyboard ? undefined : (e) => e.preventDefault()}
            onPointerDownOutside={handlePointerDownOutside}
            onFocusOutside={handleInteractOutside}
            onInteractOutside={handleInteractOutside}
            onCloseAutoFocus={onCloseAutoFocus}
          >
            {/* modal-dialog wrapper for CSS compatibility with legacy LESS styles */}
            <div className="modal-dialog flex flex-col min-h-0 flex-1">
              <div
                className={cn(
                  "modal-content flex flex-col p-6 gap-6",
                  contentClassName
                )}
              >
                {/* Wrap children in ModalStackProvider so nested modals know their depth */}
                <ModalStackProvider parentDepth={depth}>
                  {needsFallbackTitle ? (
                    <DialogPrimitive.Title className="sr-only">
                      {FALLBACK_DIALOG_TITLE}
                    </DialogPrimitive.Title>
                  ) : null}
                  {children}
                </ModalStackProvider>
              </div>
            </div>
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

// Close button component using SVG icon
interface CloseButtonProps {
  onClick?: () => void;
  className?: string;
}

function CloseButton({ onClick, className }: CloseButtonProps) {
  return (
    <DialogPrimitive.Close
      className={cn(
        "text-neutral-500 hover:text-blue-500 transition-colors p-1",
        className
      )}
      onClick={onClick}
      aria-label="Close"
      title="Close dialog"
    >
      <span className="w-3.5 h-3.5 block">
        <CloseIcon />
      </span>
    </DialogPrimitive.Close>
  );
}

// Modal.Header component
interface ModalHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  closeButton?: boolean;
  onHide?: () => void;
}

function ModalHeader({
  className,
  children,
  closeButton,
  onHide,
  ...props
}: ModalHeaderProps) {
  return (
    <div
      className={cn("modal-header flex justify-between items-start", className)}
      {...props}
    >
      <div className="flex-1">{children}</div>
      {closeButton && <CloseButton onClick={onHide} className="ml-4" />}
    </div>
  );
}

// Modal.Title component
function ModalTitle({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <DialogPrimitive.Title
      className={cn(
        "modal-title text-2xl font-semibold text-blue-700 leading-8",
        className
      )}
      {...props}
      asChild
    >
      <h4>{children}</h4>
    </DialogPrimitive.Title>
  );
}

// Modal.Body component
function ModalBody({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "modal-body text-neutral-700 text-base leading-6",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// Modal.Footer component
function ModalFooter({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "modal-footer flex justify-between items-center",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

// Attach sub-components to Modal
Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.CloseButton = CloseButton;

export { Modal, ModalHeader, ModalTitle, ModalBody, ModalFooter, CloseButton };
export type { ModalHeaderProps };
